import { useContext } from "react";
import { FaAngleUp, FaAngleDown } from "react-icons/fa";
import Select, { components } from "react-select";
import AppContext from "../../context/AppContext";

const CheckboxOption = (props) => {
    const { langMode } = useContext(AppContext);
    const isArabic = langMode === 'ar';

    return (
        <div>
            <components.Option
                {...props}
                className={`!flex justify-between items-center text-[#000000cc] border-b border-[#e0e0e0] px-3 py-2 hover:bg-gray-100 transition-all duration-150 cursor-pointer ${isArabic ? 'text-[14px]' : 'text-[16px]'}`}
            >
                <label className="flex-grow">{props.label}</label>
                {props?.isMulti && (
                    <input
                        type="checkbox"
                        checked={props.isSelected}
                        readOnly
                        className="accent-blue-500"
                        style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    />
                )}
            </components.Option>
        </div>
    );
};

const DropdownIndicator = (props) => {
    const { menuIsOpen } = props.selectProps;
    return (
        <components.DropdownIndicator {...props}>
            <span className="text-gray-400 hover:text-gray-600 transition-colors pr-2">
                {menuIsOpen ? <FaAngleUp size={16} /> : <FaAngleDown size={16} />}
            </span>
        </components.DropdownIndicator>
    );
};

const ReactSelector = ({
    maxMenuHeight = "250px",
    options,
    onChange,
    defaultValue,
    height = "36px",
    isDisabled,
    components = {},
    placeholder,
    isClearable,
    borderRadius,
    background,
    defaultMenuIsOpen,
    optionbackground,
    isMulti,
    fontSize = "16px",
    menuPortalTarget,
    overflow,
    isSearchable,
    borderColor = "#00000066",
    styles: externalStyles = {},
    dataTestId = "react-selector",
    ...rest
}) => {
    const { langMode } = useContext(AppContext);

    const mergeStyle = (key, ...args) => {
        const internal = internalStyles[key];
        const external = externalStyles[key];
        const base = typeof internal === 'function' ? internal(...args) : internal;
        const overrides = typeof external === 'function' ? external(...args) : external;
        return overrides ? { ...base, ...overrides } : base;
    };

    const internalStyles = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            height,
            minHeight: height,
            borderColor: state.isFocused || state.selectProps.menuIsOpen
                ? '#6366f1'
                : '#e5e7eb',
            boxShadow: state.isFocused || state.selectProps.menuIsOpen
                ? '0 0 0 3px rgba(99, 102, 241, 0.15)'
                : '0 1px 2px rgba(0, 0, 0, 0.04)',
            borderRadius: borderRadius || "8px",
            background: background || '#ffffff',
            fontSize,
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: '#d1d5db' },
            ...(overflow && { overflowX: overflow }),
        }),
        menu: (baseStyles) => ({
            ...baseStyles,
            width: "100% !important",
            minWidth: "100%",
            marginTop: "4px",
            marginBottom: "4px",
            border: '1px solid #e5e7eb',
            zIndex: 999,
            borderRadius: "8px",
            boxShadow: "0 10px 40px -10px rgba(0, 0, 0, 0.12), 0 4px 6px -2px rgba(0, 0, 0, 0.04)",
            overflow: 'hidden',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menuList: (baseStyles) => ({
            ...baseStyles,
            maxHeight: maxMenuHeight,
            paddingTop: "6px",
            paddingBottom: "6px",
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected
                ? optionbackground || 'rgba(99, 102, 241, 0.08)'
                : state.isFocused
                ? '#f8fafc'
                : 'transparent',
            color: state.isSelected ? '#4338ca' : '#334155',
            fontSize,
            padding: '10px 14px',
            cursor: 'pointer',
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize,
            color: "#94a3b8",
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize,
            color: "#334155",
            marginBottom: langMode === "ar" ? "10px" : "0",
            fontWeight: 500,
        }),
        input: (provided) => ({
            ...provided,
            fontSize,
            color: "#334155",
        }),
    };

    const mergedStyles = {};
    Object.keys(internalStyles).forEach((key) => {
        const internal = internalStyles[key];
        const external = externalStyles[key];
        if (typeof internal !== 'function') {
            mergedStyles[key] = external ? { ...internal, ...(typeof external === 'function' ? external({}) : external) } : internal;
        } else {
            mergedStyles[key] = (...args) => mergeStyle(key, ...args);
        }
    });
    Object.keys(externalStyles).forEach((key) => {
        if (!mergedStyles[key]) {
            mergedStyles[key] = externalStyles[key];
        }
    });

    return (
        <Select
            {...rest}
            isMulti={isMulti}
            defaultMenuIsOpen={defaultMenuIsOpen}
            isClearable={isClearable}
            className="basic-single"
            classNamePrefix="select"
            options={options}
            isDisabled={isDisabled}
            defaultValue={defaultValue}
            components={{
                Option: CheckboxOption,
                DropdownIndicator,
                IndicatorSeparator: () => null,
                ...components,
            }}
            placeholder={placeholder}
            onChange={onChange}
            isSearchable={isSearchable}
            menuPortalTarget={menuPortalTarget}
            styles={mergedStyles}
            data-testid={dataTestId}
            inputId={dataTestId}
        />
    );
};

export default ReactSelector;
